import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"
import "sweetalert2/dist/sweetalert2.min.css"

export default class extends Controller {
  static targets = [
    "trigger", "modal", "calendarContainer", "checkinDisplay", "checkoutDisplay",
    "nights", "totalPrice", "totalRow", "lines", "minNotice",
    "breakdownWrapper", "guests", "bringingPets"
  ]
  static values = {
    priceUrl: String,
    unavailableDates: Array
  }

  connect() {
    this.selectedCheckin = null
    this.selectedCheckout = null
    this.currentMonth = new Date()
    this.currentMonth.setDate(1)
    this.unavailableSet = new Set(this.unavailableDatesValue || [])
  }

  // Open the date picker modal
  openModal(event) {
    if (event) event.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
      this.renderCalendar()
    }
  }

  // Close the date picker modal
  closeModal(event) {
    if (event) event.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  // Close modal when clicking backdrop
  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }

  // Prevent clicks inside modal from closing it
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Navigate to previous month
  prevMonth(event) {
    if (event) event.preventDefault()
    this.currentMonth.setMonth(this.currentMonth.getMonth() - 1)
    this.renderCalendar()
  }

  // Navigate to next month
  nextMonth(event) {
    if (event) event.preventDefault()
    this.currentMonth.setMonth(this.currentMonth.getMonth() + 1)
    this.renderCalendar()
  }

  // Render the calendar
  renderCalendar() {
    if (!this.hasCalendarContainerTarget) return

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Get the min/max selectable dates based on check-in selection
    let minSelectableDate = null
    let maxSelectableDate = null

    if (this.selectedCheckin && !this.selectedCheckout) {
      // When selecting checkout: can't select on or before check-in, or after first blocked date
      minSelectableDate = this.selectedCheckin
      maxSelectableDate = this.findFirstUnavailableDateAfter(this.selectedCheckin)
    }

    // Create two months
    const month1 = new Date(this.currentMonth)
    const month2 = new Date(this.currentMonth)
    month2.setMonth(month2.getMonth() + 1)

    this.calendarContainerTarget.innerHTML = `
      <div class="calendar-wrapper flex gap-8">
        ${this.renderMonth(month1, today, minSelectableDate, maxSelectableDate, "left")}
        ${this.renderMonth(month2, today, minSelectableDate, maxSelectableDate, "right")}
      </div>
    `

    // Add click handlers to all day buttons
    this.calendarContainerTarget.querySelectorAll("[data-date]").forEach(btn => {
      btn.addEventListener("click", (e) => this.selectDate(e))
      btn.addEventListener("mouseenter", (e) => this.handleDayHover(e))
      btn.addEventListener("mouseleave", (e) => this.handleDayLeave(e))
    })

    // Add click handlers for navigation
    const prevBtn = this.calendarContainerTarget.querySelector("[data-action='prev']")
    const nextBtn = this.calendarContainerTarget.querySelector("[data-action='next']")
    if (prevBtn) prevBtn.addEventListener("click", (e) => this.prevMonth(e))
    if (nextBtn) nextBtn.addEventListener("click", (e) => this.nextMonth(e))
  }

  // Handle hover on day - show range preview
  handleDayHover(event) {
    const btn = event.currentTarget
    if (btn.disabled) return

    // Only show preview when check-in is selected but not checkout
    if (!this.selectedCheckin || this.selectedCheckout) return

    const hoveredDateStr = btn.dataset.date
    const hoveredDate = this.parseLocalDate(hoveredDateStr)

    // Only preview if hovering after check-in
    if (hoveredDate <= this.selectedCheckin) return

    // Highlight all dates between check-in and hovered date
    this.calendarContainerTarget.querySelectorAll("[data-date]").forEach(dayBtn => {
      const dateStr = dayBtn.dataset.date
      const date = this.parseLocalDate(dateStr)
      const wrapper = dayBtn.parentElement

      if (date > this.selectedCheckin && date < hoveredDate) {
        // In-range dates: full background on wrapper
        wrapper.classList.add("bg-slate-100")
      }
      if (date.getTime() === this.selectedCheckin.getTime()) {
        // Check-in date: gradient on wrapper
        wrapper.classList.add("bg-gradient-to-r", "from-transparent", "to-slate-100")
      }
      if (date.getTime() === hoveredDate.getTime()) {
        // Hovered date: gradient on wrapper, darker button
        wrapper.classList.add("bg-gradient-to-l", "from-transparent", "to-slate-100")
        dayBtn.classList.add("bg-slate-200")
      }
    })
  }

  // Handle mouse leave - clear preview
  handleDayLeave(event) {
    if (!this.selectedCheckin || this.selectedCheckout) return

    // Remove preview highlighting from all dates
    this.calendarContainerTarget.querySelectorAll("[data-date]").forEach(dayBtn => {
      const dateStr = dayBtn.dataset.date
      const date = this.parseLocalDate(dateStr)
      const wrapper = dayBtn.parentElement

      // Don't remove styling from check-in date if it has checkout
      if (this.selectedCheckin && this.selectedCheckout && date.getTime() === this.selectedCheckin.getTime()) return

      wrapper.classList.remove("bg-slate-100", "bg-gradient-to-r", "bg-gradient-to-l", "from-transparent", "to-slate-100")
      dayBtn.classList.remove("bg-slate-200")
    })
  }

  // Render a single month
  renderMonth(monthDate, today, minSelectableDate, maxSelectableDate, position = null) {
    const year = monthDate.getFullYear()
    const month = monthDate.getMonth()
    const monthName = monthDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })

    // Navigation arrows
    const leftArrow = position === "left" ? `
      <button type="button" data-action="prev" class="p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer absolute left-0">
        <svg class="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>
    ` : ""

    const rightArrow = position === "right" ? `
      <button type="button" data-action="next" class="p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer absolute right-0">
        <svg class="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </button>
    ` : ""

    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()

    const days = []

    // Add empty cells for padding
    for (let i = 0; i < startPadding; i++) {
      days.push('<div class="w-10 h-10"></div>')
    }

    // Add day cells
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d)
      const dateStr = this.formatDateForInput(date)
      const isToday = date.getTime() === today.getTime()
      const isPast = date < today
      const isUnavailable = this.unavailableSet.has(dateStr)
      const isBeforeMin = minSelectableDate && date <= minSelectableDate
      const isBeyondMax = maxSelectableDate && date > maxSelectableDate
      const isDisabled = isPast || isUnavailable || isBeforeMin || isBeyondMax

      const isCheckin = this.selectedCheckin && date.getTime() === this.selectedCheckin.getTime()
      const isCheckout = this.selectedCheckout && date.getTime() === this.selectedCheckout.getTime()
      const isInRange = this.selectedCheckin && this.selectedCheckout &&
        date > this.selectedCheckin && date < this.selectedCheckout

      // Wrapper classes for the range background
      let wrapperClasses = "w-10 h-10 flex items-center justify-center "
      if (isInRange) {
        wrapperClasses += "bg-slate-100 "
      }
      if (isCheckin && this.selectedCheckout) {
        wrapperClasses += "bg-gradient-to-r from-transparent to-slate-100 "
      }
      if (isCheckout) {
        wrapperClasses += "bg-gradient-to-l from-transparent to-slate-100 "
      }

      // Button classes for the circular date
      let btnClasses = "w-10 h-10 flex items-center justify-center text-sm rounded-full transition-colors "

      if (isDisabled) {
        btnClasses += "text-slate-300 cursor-not-allowed "
        if (isUnavailable) btnClasses += "line-through "
      } else {
        btnClasses += "cursor-pointer hover:bg-slate-200 "
      }

      if (isCheckin || isCheckout) {
        btnClasses += "bg-slate-900 text-white hover:bg-slate-800 "
      }

      if (isToday && !isCheckin && !isCheckout) {
        btnClasses += "border border-slate-900 "
      }

      const disabled = isDisabled ? "disabled" : ""

      days.push(`
        <div class="${wrapperClasses}">
          <button type="button"
                  class="${btnClasses}"
                  data-date="${dateStr}"
                  ${disabled}>
            ${d}
          </button>
        </div>
      `)
    }

    return `
      <div class="w-[280px]">
        <div class="relative flex items-center justify-center font-semibold text-slate-900 mb-4 h-9">
          ${leftArrow}
          <span>${monthName}</span>
          ${rightArrow}
        </div>
        <div class="grid grid-cols-7 mb-2">
          <div class="w-10 h-6 flex items-center justify-center text-xs font-medium text-slate-500">Su</div>
          <div class="w-10 h-6 flex items-center justify-center text-xs font-medium text-slate-500">Mo</div>
          <div class="w-10 h-6 flex items-center justify-center text-xs font-medium text-slate-500">Tu</div>
          <div class="w-10 h-6 flex items-center justify-center text-xs font-medium text-slate-500">We</div>
          <div class="w-10 h-6 flex items-center justify-center text-xs font-medium text-slate-500">Th</div>
          <div class="w-10 h-6 flex items-center justify-center text-xs font-medium text-slate-500">Fr</div>
          <div class="w-10 h-6 flex items-center justify-center text-xs font-medium text-slate-500">Sa</div>
        </div>
        <div class="grid grid-cols-7 gap-y-1 content-start" style="height: 264px;">
          ${days.join("")}
        </div>
      </div>
    `
  }

  // Handle date selection
  selectDate(event) {
    const btn = event.currentTarget
    if (btn.disabled) return

    const dateStr = btn.dataset.date
    const date = this.parseLocalDate(dateStr)

    if (!this.selectedCheckin || (this.selectedCheckin && this.selectedCheckout)) {
      // First click or starting over: set check-in
      this.selectedCheckin = date
      this.selectedCheckout = null
    } else {
      // Second click: set check-out
      if (date <= this.selectedCheckin) {
        // Clicked before or on check-in, restart
        this.selectedCheckin = date
        this.selectedCheckout = null
      } else {
        this.selectedCheckout = date
        this.calculateNights(this.selectedCheckin, this.selectedCheckout)
      }
    }

    this.updateDateDisplays()
    this.renderCalendar()
  }

  // Update the date display boxes
  updateDateDisplays() {
    const checkinText = this.selectedCheckin
      ? this.formatDateForTrigger(this.selectedCheckin)
      : "Add date"
    const checkoutText = this.selectedCheckout
      ? this.formatDateForTrigger(this.selectedCheckout)
      : "Add date"

    this.checkinDisplayTargets.forEach(el => {
      el.textContent = checkinText
      el.classList.toggle("text-slate-400", !this.selectedCheckin)
      el.classList.toggle("text-slate-900", !!this.selectedCheckin)
    })

    this.checkoutDisplayTargets.forEach(el => {
      el.textContent = checkoutText
      el.classList.toggle("text-slate-400", !this.selectedCheckout)
      el.classList.toggle("text-slate-900", !!this.selectedCheckout)
    })
  }

  // Find the first unavailable date after the given date
  findFirstUnavailableDateAfter(checkinDate) {
    const unavailableDates = this.unavailableDatesValue || []
    if (unavailableDates.length === 0) return null

    const sortedUnavailable = unavailableDates
      .map(dateStr => this.parseLocalDate(dateStr))
      .filter(date => date > checkinDate)
      .sort((a, b) => a - b)

    return sortedUnavailable.length > 0 ? sortedUnavailable[0] : null
  }

  calculateNights(checkin, checkout) {
    const nights = Math.ceil((checkout - checkin) / (1000 * 60 * 60 * 24))
    if (nights <= 0) return

    if (this.hasNightsTarget) {
      this.nightsTarget.textContent = `${nights} ${nights === 1 ? "night" : "nights"}`
    }

    this.selectedNights = nights
    this.fetchPrice(checkin, checkout)
  }

  fetchPrice(checkin, checkout) {
    if (!this.priceUrlValue) return

    const params = new URLSearchParams({
      checkin: this.formatDateForInput(checkin),
      checkout: this.formatDateForInput(checkout),
      guest_count: this.hasGuestsTarget ? this.guestsTarget.value : 1,
      bringing_pets: this.hasBringingPetsTarget && this.bringingPetsTarget.checked
    })

    const requestId = (this.priceRequestId || 0) + 1
    this.priceRequestId = requestId

    fetch(`${this.priceUrlValue}?${params}`, { headers: { Accept: "application/json" } })
      .then(response => response.ok ? response.json() : null)
      .then(data => {
        if (requestId !== this.priceRequestId || !data) return
        this.renderPrice(data, checkin, checkout)
      })
      .catch(() => {})
  }

  renderPrice(data, checkin, checkout) {
    this.lastPriceData = data

    if (!data.available) {
      if (this.hasBreakdownWrapperTarget) this.breakdownWrapperTarget.classList.add("hidden")
      if (this.hasTotalRowTarget) this.totalRowTarget.classList.add("hidden")
      if (this.hasMinNoticeTarget) {
        if (data.below_min_nights) {
          this.minNoticeTarget.textContent = `This listing has a ${data.min_nights} night minimum stay.`
          this.minNoticeTarget.classList.remove("hidden")
        } else {
          this.minNoticeTarget.classList.add("hidden")
        }
      }
      this.selectedTotal = null
      return
    }

    if (this.hasMinNoticeTarget) this.minNoticeTarget.classList.add("hidden")

    if (this.hasLinesTarget) {
      this.linesTarget.innerHTML = this.lineRowsHtml(data.lines)
      this.linesTarget.classList.remove("hidden")
    }
    if (this.hasBreakdownWrapperTarget) this.breakdownWrapperTarget.classList.remove("hidden")
    if (this.hasTotalPriceTarget) this.totalPriceTarget.textContent = data.total
    if (this.hasTotalRowTarget) {
      this.totalRowTarget.classList.remove("hidden")
      this.totalRowTarget.classList.add("flex")
    }

    this.selectedTotal = data.total
    this.dispatch("dates-selected", {
      detail: { checkin, checkout, nights: this.selectedNights, total: data.total }
    })
  }

  guestsChanged() {
    if (this.selectedCheckin && this.selectedCheckout) {
      this.calculateNights(this.selectedCheckin, this.selectedCheckout)
    }
  }

  petsChanged() {
    if (this.selectedCheckin && this.selectedCheckout) {
      this.calculateNights(this.selectedCheckin, this.selectedCheckout)
    }
  }

  lineRowsHtml(lines) {
    return lines.map(line => `
      <div class="flex justify-between">
        <span class="text-slate-600">${line.label}</span>
        <span class="text-slate-900">${line.amount}</span>
      </div>
    `).join("")
  }

  openReservationModal(event) {
    if (!this.selectedCheckin || !this.selectedCheckout) {
      event.preventDefault()
      event.stopPropagation()
      Swal.fire({
        title: "Select your dates",
        text: "Please select check-in and check-out dates first.",
        icon: "info",
        confirmButtonText: "Got it",
        buttonsStyling: false,
        customClass: { confirmButton: "btn-md" }
      })
      return
    }

    const checkinField = document.getElementById("reservation-checkin")
    const checkoutField = document.getElementById("reservation-checkout")
    const checkinDisplay = document.getElementById("reservation-checkin-display")
    const checkoutDisplay = document.getElementById("reservation-checkout-display")
    const totalDisplay = document.getElementById("reservation-total-display")

    if (checkinField) checkinField.value = this.formatDateForInput(this.selectedCheckin)
    if (checkoutField) checkoutField.value = this.formatDateForInput(this.selectedCheckout)
    if (checkinDisplay) checkinDisplay.textContent = this.formatDateForDisplay(this.selectedCheckin)
    if (checkoutDisplay) checkoutDisplay.textContent = this.formatDateForDisplay(this.selectedCheckout)

    const data = this.lastPriceData
    if (totalDisplay && data && data.available) totalDisplay.textContent = data.total

    const guestCountField = document.getElementById("reservation-guest-count")
    const bringingPetsField = document.getElementById("reservation-bringing-pets")
    if (guestCountField) guestCountField.value = this.hasGuestsTarget ? this.guestsTarget.value : 1
    if (bringingPetsField) bringingPetsField.value = this.hasBringingPetsTarget && this.bringingPetsTarget.checked

    const breakdownSection = document.getElementById("reservation-breakdown")
    if (breakdownSection && data && data.available) {
      breakdownSection.innerHTML = this.lineRowsHtml(data.lines)
      breakdownSection.classList.remove("hidden")
    }

    const modal = document.getElementById("reservation-modal")
    if (modal && typeof modal.showModal === "function" && !modal.open) {
      modal.showModal()
    }
  }

  parseLocalDate(dateStr) {
    const [year, month, day] = dateStr.split("-").map(Number)
    return new Date(year, month - 1, day)
  }

  formatDateForInput(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  formatDateForDisplay(date) {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  formatDateForTrigger(date) {
    return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })
  }

  clearDates(event) {
    if (event) event.preventDefault()

    this.selectedCheckin = null
    this.selectedCheckout = null
    this.selectedNights = null
    this.selectedTotal = null
    this.lastPriceData = null

    this.updateDateDisplays()
    this.renderCalendar()

    if (this.hasNightsTarget) this.nightsTarget.textContent = ""
    if (this.hasTotalPriceTarget) this.totalPriceTarget.textContent = ""
    if (this.hasBreakdownWrapperTarget) this.breakdownWrapperTarget.classList.add("hidden")
    if (this.hasTotalRowTarget) this.totalRowTarget.classList.add("hidden")
    if (this.hasMinNoticeTarget) this.minNoticeTarget.classList.add("hidden")
  }
}
